import { match } from "ts-pattern";

import { dayjs } from "@thalamos/common";

import type { Location } from "../../types/location.js";
import type {
  UnknownableValue,
  UnknownableValueAndLabel,
  Value,
  ValueAndLabel,
} from "../../types/workflow/index.js";
import { WorkflowVersionSchema } from "../../types/workflowVersion.js";

export const notApplicable = "N/A";
const unknown = "Unknown";

/**
 * Ensures that T only contains the keys of Reference.
 */
export type ExactlyLike<T, Reference> =
  Exclude<keyof T, keyof Reference> extends never ? T : never;

/**
 * Ensures that T only contains a value, otherwise you are probably looking
 * for a different type like the Label or the Unknownable.
 */
export type ValueLike<T> = ExactlyLike<T, Value<string>>;

export const getValue = <T extends Value<string>>(
  data: ValueLike<T> | null | undefined,
): string => {
  return data?.value ?? notApplicable;
};

/**
 * Ensures that T only contains a value or unknown, otherwise you are probably looking
 * for a different type like the Label or the Unknownable.
 */
type UnknownableValueLike<T> = ExactlyLike<T, UnknownableValue<string>>;

export const getUnknownableValue = <T extends UnknownableValue<string>>(
  data: UnknownableValueLike<T> | null | undefined,
): string => {
  return data?.isUnknown ? unknown : (data?.value ?? notApplicable);
};

/**
 * Ensures that T only contains a value or a valueLabel, otherwise you are probably looking
 * for a different type like the Unknownable.
 */
type ValueLabelLike<T> = ExactlyLike<T, ValueAndLabel<string>>;

export const getValueLabel = <T extends ValueAndLabel<string>>(
  data: ValueLabelLike<T> | null | undefined,
): string => {
  return data?.valueLabel ?? notApplicable;
};

/**
 * Renders a picklist answer, substituting the free text the officer typed when
 * they chose "other" - printing the word "Other" would lose what they said.
 *
 * A picklist offering no "other" option never reaches that branch, so this is
 * safe for any picklist, paired or not.
 */
export const getValueLabelOrOther = <
  T extends ValueAndLabel<string>,
  O extends Value<string>,
>(
  data: ValueLabelLike<T> | null | undefined,
  otherData: ValueLike<O> | null | undefined,
): string => {
  return data?.value === "other" ? getValue(otherData) : getValueLabel(data);
};

/**
 * Ensures that T only contains a value, valueLabel, and isUnknown.
 */
type UnknownableValueLabelLike<T> = ExactlyLike<
  T,
  UnknownableValueAndLabel<string>
>;

export const getUnknownableValueLabel = <
  T extends UnknownableValueAndLabel<string>,
>(
  data: UnknownableValueLabelLike<T> | null | undefined,
): string => {
  return data?.isUnknown ? unknown : (data?.valueLabel ?? notApplicable);
};

/**
 * A police dispatch reference is quoted as the number and the day it was raised
 * together, so the two are printed as one value. Forces differ in how they
 * write the day, so a workflow picks the convention its own force reads back.
 * An incident that carries no day prints the number alone.
 */
const getDispatchReference = <T extends Value<string>, D extends Value<string>>(
  dispatchNumber: ValueLike<T> | null | undefined,
  dispatchNumberCreated: ValueLike<D> | null | undefined,
  separator: string,
  dayFormat: string,
): string => {
  const number = getValue(dispatchNumber);
  const raisedOn = dispatchNumberCreated?.value;

  // dayjs reads an absent day as the present moment, so the day has to be
  // known before it is parsed; and a day appended to the "N/A" fallback would
  // read as a real reference.
  if (dispatchNumber?.value === undefined || raisedOn === undefined) {
    return number;
  }

  const raisedOnAsDate = dayjs.utc(raisedOn);

  return raisedOnAsDate.isValid()
    ? `${number}${separator}${raisedOnAsDate
        .tz("Europe/London")
        .format(dayFormat)
        .toUpperCase()}`
    : number;
};

/** Quotes a dispatch reference with the month named, e.g. 3324/15SEP26. */
export const getDispatchReferenceWithNamedMonth = <
  T extends Value<string>,
  D extends Value<string>,
>(
  dispatchNumber: ValueLike<T> | null | undefined,
  dispatchNumberCreated: ValueLike<D> | null | undefined,
): string =>
  getDispatchReference(dispatchNumber, dispatchNumberCreated, "/", "DDMMMYY");

/** Quotes a dispatch reference with the month in digits, e.g. 3324-150926. */
export const getDispatchReferenceWithNumericMonth = <
  T extends Value<string>,
  D extends Value<string>,
>(
  dispatchNumber: ValueLike<T> | null | undefined,
  dispatchNumberCreated: ValueLike<D> | null | undefined,
): string =>
  getDispatchReference(dispatchNumber, dispatchNumberCreated, "-", "DDMMYY");

/**
 * Quotes a dispatch reference the way the force that raised it writes one:
 * the convention belongs to the force, not to the surface it is shown on.
 */
export const getDispatchReferenceForWorkflow = <
  T extends Value<string>,
  D extends Value<string>,
>(
  workflowVersion: string,
  dispatchNumber: ValueLike<T> | null | undefined,
  dispatchNumberCreated: ValueLike<D> | null | undefined,
): string => {
  const version = WorkflowVersionSchema.safeParse(workflowVersion);

  // We cannot tell which force raised a version we do not recognise, and a day
  // appended in another force's convention would quote a reference that force
  // never issued.
  if (!version.success) {
    return getValue(dispatchNumber);
  }

  return match(version.data)
    .with(
      WorkflowVersionSchema.enum.BTP_1_0,
      WorkflowVersionSchema.enum.BTP_1_1,
      WorkflowVersionSchema.enum.BTP_1_2,
      () =>
        getDispatchReferenceWithNumericMonth(
          dispatchNumber,
          dispatchNumberCreated,
        ),
    )
    .with(
      WorkflowVersionSchema.enum.MET_1_0,
      WorkflowVersionSchema.enum.COLP_1_0,
      () =>
        getDispatchReferenceWithNamedMonth(
          dispatchNumber,
          dispatchNumberCreated,
        ),
    )
    .exhaustive();
};

export const getValueAsDate = <T extends Value<string>>(
  data: ValueLike<T> | null | undefined,
): string => {
  if (!data) {
    return notApplicable;
  }

  const valueAsDate = dayjs.utc(data.value);
  const valueAsString = valueAsDate.isValid()
    ? valueAsDate.tz("Europe/London").format("DD/MM/YYYY")
    : notApplicable;

  return valueAsString;
};

export const getUnknownableValueAsDate = <T extends UnknownableValue<string>>(
  data: UnknownableValueLike<T> | null | undefined,
): string => {
  if (!data) {
    return notApplicable;
  }

  if (data.isUnknown) {
    return unknown;
  }

  const valueAsDate = dayjs.utc(data.value);
  const valueAsString = valueAsDate.isValid()
    ? valueAsDate.tz("Europe/London").format("DD/MM/YYYY")
    : notApplicable;

  return valueAsString;
};

export const getValueAsTime = <T extends Value<string>>(
  data: ValueLike<T> | null | undefined,
): string => {
  if (!data) {
    return notApplicable;
  }

  const valueAsDate = dayjs.utc(data.value);
  const valueAsString = valueAsDate.isValid()
    ? valueAsDate.tz("Europe/London").format("HH:mm")
    : notApplicable;

  return valueAsString;
};

export const getValueAsDateTime = <T extends Value<string>>(
  data: ValueLike<T> | null | undefined,
): string => {
  if (!data) {
    return notApplicable;
  }

  const valueAsDate = dayjs.utc(data.value);
  const valueAsString = valueAsDate.isValid()
    ? valueAsDate.tz("Europe/London").format("DD/MM/YYYY HH:mm")
    : notApplicable;

  return valueAsString;
};

export const getValueAsLocation = (
  data: Value<Location> | null | undefined,
): string => {
  if (!data) {
    return notApplicable;
  }

  return data.value.userProvided.formattedValue;
};

export const getUnknownableValueAsLocation = (
  data: UnknownableValue<Location> | null | undefined,
): string => {
  if (!data) {
    return notApplicable;
  }

  if (data.isUnknown) {
    return unknown;
  }

  return data.value?.userProvided.formattedValue ?? notApplicable;
};
